/**
 * Helpers for `/device-login`, the browser page a local app opens to sign
 * in. The page hands a custom token to the `redirect_uri` it was opened with,
 * so that URI has to be a listener on this machine.
 */

import type { OrgProvidersResponse } from "./org-session"

// The desktop app listens on `http://localhost:<port>/device-token`, see
// `components/Login.vue` and `hoppscotch-desktop/src-tauri/src/server.rs`.
// `@hoppscotch/mcp-server` listens on `http://localhost:<port>/callback/<nonce>`,
// so the path is left to the caller.
const LOOPBACK_HOSTS = ["localhost", "127.0.0.1", "[::1]"]

/**
 * Returns true when `uri` is a plain-HTTP listener on this machine with an
 * explicit port, so the page never sends a token to any other host a
 * crafted link names.
 */
export const isLoopbackRedirectURI = (uri: string | null): boolean => {
  if (!uri) return false

  let url: URL
  try {
    url = new URL(uri)
  } catch {
    return false
  }

  return (
    url.protocol === "http:" &&
    LOOPBACK_HOSTS.includes(url.hostname) &&
    url.port !== "" &&
    url.username === "" &&
    url.password === ""
  )
}

// A single DNS label, which is what an org subdomain is (`acme` for
// `acme.hoppscotch.io`).
const ORG_DOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/

/**
 * Reads the org the desktop app is signing in to from the `org` query
 * param, lowercased, or null when it is absent or not a single DNS label.
 */
export const parseDeviceLoginOrg = (value: string | null): string | null => {
  if (!value) return null

  const org = value.trim().toLowerCase()
  return ORG_DOMAIN_PATTERN.test(org) ? org : null
}

const deviceLoginParams = (
  redirectURI: string | null,
  orgDomain: string | null
): URLSearchParams => {
  const params = new URLSearchParams()
  if (redirectURI) params.set("redirect_uri", redirectURI)
  if (orgDomain) params.set("org", orgDomain)
  return params
}

/**
 * Builds the `/device-login` link the desktop app opens in the browser. An
 * org window passes its org so the page can run the org's own sign-in
 * before issuing the device token. The link is joined as a string, so a
 * build whose `VITE_BASE_URL` is empty still gets a link to show and copy.
 */
export const buildDeviceLoginURL = (
  baseURL: string,
  redirectURI: string,
  orgDomain: string | null
): string =>
  `${baseURL}/device-login?${deviceLoginParams(redirectURI, orgDomain)}`

/**
 * The address an org email link sent from `/device-login` returns to. It
 * keeps only the params the desktop app opened the page with, so the
 * `oobCode` of a used link never comes back on a reload.
 */
export const deviceLoginReturnURL = (
  location: { origin: string; pathname: string },
  redirectURI: string | null,
  orgDomain: string | null
): string =>
  `${location.origin}${location.pathname}?${deviceLoginParams(redirectURI, orgDomain)}`

/**
 * Looks up the org's sign-in methods for `/device-login`. The org ID is
 * resolved here, before any button shows, so a provider click opens the
 * popup with no await in between and the browser doesn't block it. A
 * fallback answer means the lookup failed and the org's SSO is unknown, so
 * it throws for the page to offer a retry.
 */
export const lookUpDeviceLoginOrg = async (
  orgDomain: string,
  fetchProviders: (domain: string) => Promise<OrgProvidersResponse>,
  resolveOrgID: (domain: string) => Promise<string | null>
): Promise<{ providers: OrgProvidersResponse; orgID: string | null }> => {
  const providers = await fetchProviders(orgDomain)

  if (providers.isFallback) {
    throw new Error("organization/base_info_not_available")
  }

  if (!providers.gcipTenantID) return { providers, orgID: null }

  const orgID = providers.orgID ?? (await resolveOrgID(orgDomain))
  if (!orgID) throw new Error("organization/base_info_not_available")

  return { providers, orgID }
}

/**
 * Tells the page to offer the regular login after an SSO sign-in fails. The
 * backend joins an org only the account that is signed in while it happens,
 * so for an email that already has a Hoppscotch account it answers
 * `auth/link_required` until that account signs in here, after which the
 * provider button sends its token with the sign-in.
 */
export const needsAccountSignIn = (
  error: unknown,
  signedIn: boolean
): boolean => {
  const code =
    (error as { code?: unknown } | null)?.code ??
    (error instanceof Error ? error.message : null)

  return !signedIn && code === "auth/link_required"
}

/**
 * Whether the signed-in account is the one an email link was sent to. The
 * link's sign-in sends a session only for that account, so the session the
 * page ends with belongs to the link's recipient.
 */
export const isLinkRecipient = (
  signedInEmail: string | null | undefined,
  linkEmail: string
): boolean =>
  !!signedInEmail &&
  signedInEmail.trim().toLowerCase() === linkEmail.trim().toLowerCase()

/**
 * Whether the page still needs the org's own sign-in. A sign-in verifies the
 * account Firebase had signed in when it finished, so signing out or
 * switching accounts asks for it again, ∵ the desktop session goes to the
 * account signed in when the login is confirmed and an org enforcing SSO
 * rejects one that never signed in through it. A tenant that hasn't enabled
 * the provider falls back to the regular login whoever signs in.
 */
export const orgSignInPending = (
  hasTenant: boolean,
  regularLoginFallback: boolean,
  signedInUID: string | null,
  verifiedUID: string | null
): boolean =>
  hasTenant &&
  !regularLoginFallback &&
  (verifiedUID === null || signedInUID !== verifiedUID)

/**
 * Whether the org has a sign-in of its own to offer here, a GCIP tenant
 * with at least one provider. A tenant that lists no provider takes the
 * regular login, as the web login offers its default providers for an empty
 * list.
 */
export const hasOrgSignIn = (providers: OrgProvidersResponse | null): boolean =>
  !!providers?.gcipTenantID && providers.providers.length > 0

/**
 * The UID an org sign-in verifies, read from the Firebase user once the
 * exchange has finished. Another tab can switch the session while the popup
 * or the link is pending, and with a session open the backend records the
 * SSO sign-in for the org account and answers `ok` without comparing the
 * two, so the user is checked against the address that signed in to the org
 * and only that checked user's UID is recorded. Where the exchange signed
 * the main app in with a custom token, the user is the org account even when
 * the provider returned no address, and anywhere else a missing address
 * verifies nobody.
 */
export const checkedAccount = (
  user: { uid: string; email: string | null } | null,
  signedInEmail: string | null | undefined,
  signedInByExchange: boolean
): string | null => {
  if (!user) return null
  if (!signedInEmail) return signedInByExchange ? user.uid : null
  return isLinkRecipient(user.email, signedInEmail) ? user.uid : null
}
